// thumbnail/index.ts — public surface for the thumbnail bundle.
//
// Output is an IIFE (build-thumbnail-bundle.mjs) loaded as a footer <script>. It:
//   • attaches window.FtThumbnail = { build, selectUrl } for ad-hoc callers, and
//   • defines + self-runs window.ftMountThumbnails(root?), the placeholder scanner.
//
// Mount contract (mirrors the rune-icon mount, includes/functions/rune_icon/ft_rune_icon_mount_inc.php):
// PHP's ft_thumbnail_mount() emits
//   <span class="ft-thumb-mount"><script type="application/json" class="ft-thumb-blob">{…ThumbnailData…}</script></span>
// The JSON-in-a-script survives innerHTML insertion (so AJAX-injected panels work). The scanner reads the
// blob, builds the node, and replaces the placeholder's children — idempotently.
//
// AJAX re-hydration is by EXPLICIT scoped re-invocation (ftMountThumbnails(scope)) next to the existing
// ftMountRuneIcons / ftMountListDisplays calls — deliberately NOT a body-wide MutationObserver, which on
// heavy pages (creature sheet + XZ widget) fires on every mutation and starves async info-panel rendering
// (see includes/js-src/list-display/index.ts).

import { buildThumbnailNode, selectThumbnailUrl, type ThumbnailData } from './core';

declare global {
    interface Window {
        FtThumbnail?: {
            build: (data: ThumbnailData) => HTMLElement;
            selectUrl: (data: ThumbnailData) => string | null;
        };
        ftMountThumbnails?: (root?: Element | Document) => void;
    }
}

// Scan `root` (default document) for un-mounted thumbnail placeholders, render each from its JSON blob.
function ftMountThumbnails(root?: Element | Document): void {
    const scope: Element | Document = root || document;
    const mounts = scope.querySelectorAll('.ft-thumb-mount:not([data-thumb-mounted])');
    for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i] as HTMLElement;
        el.setAttribute('data-thumb-mounted', '1'); // mark first so a thrown render never loops on rescan
        const blob = el.querySelector('script.ft-thumb-blob');
        if (!blob) continue;
        try {
            const data = JSON.parse(blob.textContent || 'null') as ThumbnailData | null;
            if (!data) continue;
            el.replaceChildren(buildThumbnailNode(data)); // drops the JSON script
        } catch (e) {
            console.warn('ftMountThumbnails: mount failed', e);
        }
    }
}

window.FtThumbnail = { build: buildThumbnailNode, selectUrl: selectThumbnailUrl };
window.ftMountThumbnails = ftMountThumbnails;

// Render page-load mounts as soon as the bundle executes (it's a footer <script>, so every mount above it
// is already parsed), and again on DOMContentLoaded for any that came after. A successful mount sets the
// data-thumb-mounted flag, so the second pass is a no-op on already-rendered placeholders.
if (document.readyState !== 'loading') {
    ftMountThumbnails();
} else {
    document.addEventListener('DOMContentLoaded', () => ftMountThumbnails());
}
